import { CSSProperties } from 'react'
import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'

import { Flex } from './Flex'
import HelpWithTooltip from './HelpWithTooltip'
import { InputWithIcons, InputWithIconsProps } from './Input'
import { ControlErrorMessage, ControlLabel, TooltipText } from './Typography'

const errorAppearAnimation = keyframes`
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const AnimatedErrorMessage = styled(ControlErrorMessage)`
  animation: ${errorAppearAnimation} 0.3s ease-out forwards;
`

export type LabeledInputProps = {
  wrapperStyle?: CSSProperties
  label?: JSX.Element | string
  error?: string
  hint?: JSX.Element | string
} & InputWithIconsProps

export default function LabeledInput({
  label,
  wrapperStyle,
  className,
  iconLeft,
  iconRight,
  error,
  hint,
  ...inputProps
}: LabeledInputProps) {
  return (
    <Flex
      flexDirection="column"
      gap="0.625rem"
      width="100%"
      className={className}
      style={wrapperStyle}
    >
      {typeof label === 'string' ? (
        <Flex gap="0.5rem">
          <ControlLabel>{label}</ControlLabel>
          {hint && (
            <HelpWithTooltip
              fontSize="10px"
              size="1rem"
              tooltipContent={<TooltipText>{hint}</TooltipText>}
            />
          )}
        </Flex>
      ) : (
        label
      )}
      <Flex flexDirection="column" gap="0.3125rem" width="100%">
        <InputWithIcons
          iconLeft={iconLeft}
          iconRight={iconRight}
          hasError={!!error}
          {...inputProps}
        />
        {error && <AnimatedErrorMessage>{error}</AnimatedErrorMessage>}
      </Flex>
    </Flex>
  )
}
